import axios from "axios";
import React, { useState, useEffect } from "react";
import BarLoader from "react-spinners/BarLoader";
import styled from "styled-components";
import { useJobStatus } from "../../../hooks/useJobStatus";
import { handleScraperError } from "../../../utils/errorHandler";
import useMenuCategorization from "../restaurantBuilder/restaurantBuilder2/shared/useMenuCategorization";
import MenuCategorizationPanel from "../restaurantBuilder/restaurantBuilder2/shared/MenuCategorizationPanel";
import FailureReportModal from "../restaurantBuilder/restaurantBuilder2/FailureReportModal";

// Panel-specific styled components
const PanelContainer = styled.div`
  padding: 24px;
`;

const UrlPromptContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const UrlInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: #1565c0;
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.1);
  }
`;

const ScrapeButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background: #1565c0;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #0d47a1;
  }

  &:disabled {
    background: #bdbdbd;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #616161;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #bdbdbd;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const ProgressText = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #616161;
`;

const RecoveryContainer = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const RecoveryMessage = styled.div`
  font-size: 16px;
  color: #d32f2f;
  margin-bottom: 16px;
`;

const RecoveryButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SummaryBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryBar = styled.div`
  flex: 1;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  font-size: 14px;
  color: #757575;
`;

const FlagButton = styled.button`
  background: none;
  border: none;
  color: #9e9e9e;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  transition: color 0.2s;

  &:hover {
    color: #e65100;
  }
`;

const ApplyButton = styled.button`
  display: block;
  width: 100%;
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 24px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #e0e0e0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BottomActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ReportButton = styled.button`
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #e65100;
  background: white;
  border: 1px solid #ffcc80;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fff3e0;
    border-color: #ffb74d;
  }
`;

const MenuRefreshPanel = ({ websiteUrl, restaurantName, onApply, onCancel, setAlert }) => {
  // Panel state: prompt-url | loading | categorizing
  const [panelState, setPanelState] = useState(
    websiteUrl ? "loading" : "prompt-url"
  );
  const [urlInput, setUrlInput] = useState("");
  const [scrapeUrl, setScrapeUrl] = useState(null);
  const [hasStartedScrape, setHasStartedScrape] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [predictedOptions, setPredictedOptions] = useState({
    GUARANTEED_VEGAN: [],
    PROBABLY_VEGAN: [],
    MAYBE_VEGAN: [],
    PROBABLY_NOT_VEGAN: [],
  });
  const [contributingUrls, setContributingUrls] = useState([]);
  const [veganLegend, setVeganLegend] = useState([]);
  const [fullMenuVeganSignal, setFullMenuVeganSignal] = useState(null);
  const [failureReportModalOpen, setFailureReportModalOpen] = useState(false);

  const handlePredictedOptionsChange = (updater) => {
    setPredictedOptions(updater);
  };

  const categorization = useMenuCategorization(
    predictedOptions,
    handlePredictedOptionsChange
  );

  const mergeOptions = (current, newItems) => {
    const existing = new Set(current.map((item) => item.option));
    const merged = [...current];
    for (const item of newItems) {
      if (!existing.has(item.option)) {
        merged.push(item);
        existing.add(item.option);
      }
    }
    return merged;
  };

  const {
    status: jobStatus,
    progress: jobProgress,
    progressMessage: jobProgressMessage,
    error: jobError,
    timedOut: jobTimedOut,
  } = useJobStatus(jobId, {
    onStatusUpdate: (data) => {
      if (data.result && data.result.predictedOptions) {
        setPredictedOptions((current) => {
          const newPredicted = data.result.predictedOptions;
          return {
            GUARANTEED_VEGAN: mergeOptions(
              current.GUARANTEED_VEGAN || [],
              newPredicted.GUARANTEED_VEGAN || []
            ),
            PROBABLY_VEGAN: mergeOptions(
              current.PROBABLY_VEGAN || [],
              newPredicted.PROBABLY_VEGAN || []
            ),
            MAYBE_VEGAN: mergeOptions(
              current.MAYBE_VEGAN || [],
              newPredicted.MAYBE_VEGAN || []
            ),
            PROBABLY_NOT_VEGAN: mergeOptions(
              current.PROBABLY_NOT_VEGAN || [],
              newPredicted.PROBABLY_NOT_VEGAN || []
            ),
          };
        });
        if (data.result.contributingUrls) {
          setContributingUrls(data.result.contributingUrls);
        }
        if (data.result.predictedOptions.veganLegend) {
          setVeganLegend(data.result.predictedOptions.veganLegend);
        }
        if (data.result.predictedOptions.fullMenuVeganSignal) {
          setFullMenuVeganSignal(data.result.predictedOptions.fullMenuVeganSignal);
        }
      }
    },
    onComplete: (result) => {
      if (result && result.predictedOptions) {
        setPredictedOptions(result.predictedOptions);
        if (result.predictedOptions.veganLegend) {
          setVeganLegend(result.predictedOptions.veganLegend);
        }
        if (result.predictedOptions.fullMenuVeganSignal) {
          setFullMenuVeganSignal(result.predictedOptions.fullMenuVeganSignal);
        }
      }
      if (result && result.contributingUrls) {
        setContributingUrls(result.contributingUrls);
      }
      setPanelState("categorizing");
    },
    onError: (error) => {
      console.log("Menu refresh scraper error:", error);
      // Stay in loading state - recovery UI will show
    },
    autoConnect: !!jobId,
  });

  const startScraping = async (url) => {
    const cleanUrl = url.replace(/(^\w+:|^)\/\//, "");
    setScrapeUrl(url);
    setPanelState("loading");
    setPredictedOptions({
      GUARANTEED_VEGAN: [],
      PROBABLY_VEGAN: [],
      MAYBE_VEGAN: [],
      PROBABLY_NOT_VEGAN: [],
    });
    setContributingUrls([]);
    setVeganLegend([]);
    setFullMenuVeganSignal(null);

    try {
      const response = await axios.post(
        "/api/restaurant_builder/process_url_async",
        { url: cleanUrl }
      );
      setJobId(response.data.jobId);
    } catch (e) {
      console.log(e);
      handleScraperError(e, setAlert);
      onCancel();
    }
  };

  // Auto-start scraping if we have a website URL
  useEffect(function autoStartScrapingFromWebsiteUrl() {
    if (websiteUrl && !hasStartedScrape) {
      setHasStartedScrape(true);
      startScraping(websiteUrl);
    }
  }, [websiteUrl, hasStartedScrape]);

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      startScraping(urlInput.trim());
    }
  };

  const handleRetry = () => {
    setJobId(null);
    if (scrapeUrl) {
      startScraping(scrapeUrl);
    }
  };

  const handleCancel = () => {
    setJobId(null);
    setScrapeUrl(null);
    setUrlInput("");
    setPredictedOptions({
      GUARANTEED_VEGAN: [],
      PROBABLY_VEGAN: [],
      MAYBE_VEGAN: [],
      PROBABLY_NOT_VEGAN: [],
    });
    setContributingUrls([]);
    setVeganLegend([]);
    setFullMenuVeganSignal(null);
    categorization.resetCategories();
    onCancel();
  };

  const handleApply = () => {
    const { allMenuItems, veganOptions } = categorization.buildMenuResult();
    onApply(veganOptions, allMenuItems, scrapeUrl || websiteUrl);
  };

  // Determine recovery state
  const showRecovery =
    panelState === "loading" && (jobTimedOut || !!jobError);

  const failureReportModalElement = (
    <FailureReportModal
      isOpen={failureReportModalOpen}
      onClose={() => setFailureReportModalOpen(false)}
      restaurantName={restaurantName || ""}
      restaurantWebsite={websiteUrl || ""}
      scrapingMetadata={{ source: "menu-refresh", scrapeUrl }}
      onReportSubmitted={(result) => {
        setFailureReportModalOpen(false);
        if (result.jobId) {
          setJobId(result.jobId);
          setPanelState("loading");
        }
      }}
      setAlert={setAlert}
    />
  );

  // PROMPT-URL state
  if (panelState === "prompt-url") {
    return (
      <PanelContainer>
        <div style={{ marginBottom: "12px", fontSize: "14px", color: "#616161" }}>
          No website URL on file. Enter a menu URL to scrape:
        </div>
        <UrlPromptContainer>
          <UrlInput
            type="text"
            placeholder="https://example.com/menu"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleUrlSubmit();
              if (e.key === "Escape") handleCancel();
            }}
            autoFocus
          />
          <ScrapeButton
            type="button"
            onClick={handleUrlSubmit}
            disabled={!urlInput.trim()}
          >
            Scrape
          </ScrapeButton>
          <CancelButton type="button" onClick={handleCancel}>
            Cancel
          </CancelButton>
        </UrlPromptContainer>
      </PanelContainer>
    );
  }

  // LOADING state
  if (panelState === "loading") {
    if (showRecovery) {
      return (
        <PanelContainer>
          <RecoveryContainer>
            <RecoveryMessage>
              {jobTimedOut
                ? "Menu analysis seems to be taking too long. The server may not be responding."
                : "Something went wrong while analyzing the menu."}
            </RecoveryMessage>
            <RecoveryButtons>
              <ScrapeButton type="button" onClick={handleRetry}>
                Retry
              </ScrapeButton>
              <ReportButton type="button" onClick={() => setFailureReportModalOpen(true)}>
                Report Issue
              </ReportButton>
              <CancelButton type="button" onClick={handleCancel}>
                Cancel
              </CancelButton>
            </RecoveryButtons>
          </RecoveryContainer>
          {failureReportModalElement}
        </PanelContainer>
      );
    }

    return (
      <PanelContainer>
        <LoadingContainer>
          <BarLoader
            loading={true}
            color="#1565c0"
            height="8px"
            cssOverride={{ margin: "0 auto 16px", width: "100%" }}
          />
          <ProgressText>
            {jobProgressMessage || "Analyzing menu..."}{" "}
            {jobProgress > 0 && `(${jobProgress}%)`}
          </ProgressText>
        </LoadingContainer>
        <div style={{ textAlign: "center" }}>
          <CancelButton type="button" onClick={handleCancel}>
            Cancel
          </CancelButton>
        </div>
      </PanelContainer>
    );
  }

  // CATEGORIZING state
  return (
    <PanelContainer>
      <SummaryBarContainer>
        <SummaryBar>
          Total items: {categorization.totalItems} {" \u2022 "} Marked as vegan:{" "}
          {categorization.veganCount}
        </SummaryBar>
        <FlagButton
          type="button"
          onClick={() => setFailureReportModalOpen(true)}
          title="Report processing failure"
          aria-label="Report processing failure"
        >
          <i className="fas fa-flag" />
        </FlagButton>
      </SummaryBarContainer>

      <MenuCategorizationPanel
        predictedOptions={predictedOptions}
        categorization={categorization}
        editable
        urlIndexMap={contributingUrls.length > 0 ? contributingUrls.reduce((map, url, i) => { map[url] = i + 1; return map; }, {}) : undefined}
        veganLegend={veganLegend}
        contributingUrls={contributingUrls}
        fullMenuVeganSignal={fullMenuVeganSignal}
        onDismissFullyVegan={fullMenuVeganSignal?.detected ? async () => {
          const items = (predictedOptions.GUARANTEED_VEGAN || [])
            .filter((item) => item.source === "fully_vegan_restaurant")
            .map((item) => ({
              option: item.option,
              description: item.description || "",
              section: item.section,
            }));

          const res = await axios.post("/api/restaurant_builder/reclassify", { items });
          const reclassified = res.data;

          setPredictedOptions({
            GUARANTEED_VEGAN: (predictedOptions.GUARANTEED_VEGAN || []).filter(
              (item) => item.source !== "fully_vegan_restaurant"
            ),
            PROBABLY_VEGAN: [
              ...(predictedOptions.PROBABLY_VEGAN || []),
              ...(reclassified.PROBABLY_VEGAN || []),
            ],
            MAYBE_VEGAN: [
              ...(predictedOptions.MAYBE_VEGAN || []),
              ...(reclassified.MAYBE_VEGAN || []),
            ],
            PROBABLY_NOT_VEGAN: [
              ...(predictedOptions.PROBABLY_NOT_VEGAN || []),
              ...(reclassified.PROBABLY_NOT_VEGAN || []),
            ],
          });
          setFullMenuVeganSignal(null);
        } : undefined}
      />

      <BottomActions>
        <ApplyButton
          type="button"
          onClick={handleApply}
          disabled={categorization.veganCount === 0}
        >
          Apply Refreshed Menu ({categorization.veganCount} vegan items)
        </ApplyButton>
      </BottomActions>
      <div style={{ textAlign: "center", marginTop: "12px" }}>
        <CancelButton type="button" onClick={handleCancel}>
          Cancel
        </CancelButton>
      </div>
      {failureReportModalElement}
    </PanelContainer>
  );
};

export default MenuRefreshPanel;
